<template>
  <div
    class="rs-avatar"
    :class="avatarClasses"
    @click="$emit('click')"
  >
    <i
      v-if="icon"
      :class="`${weight} fa-${icon}`"
    />
    <img
      v-if="image"
      :src="image"
    >
    <slot />
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';

export default defineComponent({
  name: 'RsAvatar',

  props: {
    /**
        * Changes border-radius to reflect global settings.
        */
    square: {
      type: Boolean,
      default: false,
    },
    /**
        * Use FontAwesome icon name (https://fontawesome.com/icons?d=gallery)
        */
    icon: {
      type: String,
      default: '',
    },
    /**
        * Use FontAwesome weight classes (far, fal, fas). Default is fal
        */
    weight: {
      type: String,
      default: 'fal',
    },
    /**
        * The image source.
        */
    image: {
      type: String,
      default: '',
    },
    /**
        * Color variants: primary | secondary | success | warning | error | info | dark | light
        *
        */
    soft: {
      type: Boolean,
      default: false,
    },
    variant: {
      type: String,
      default: 'light',
      validator: (prop?: string) => [
        'primary',
        'secondary',
        'success',
        'warning',
        'error',
        'info',
        'dark',
        'light',
      ].includes(prop ?? ''),
    },
  },

  emits: ['click'],
  computed: {
    avatarClasses(): Record<string, boolean> {
      return {
        'rs-avatar--square': this.square,
        'rs-avatar--soft': this.soft,
        'rs-avatar--primary': this.variant === 'primary',
        'rs-avatar--secondary': this.variant === 'secondary',
        'rs-avatar--success': this.variant === 'success',
        'rs-avatar--warning': this.variant === 'warning',
        'rs-avatar--error': this.variant === 'error',
        'rs-avatar--info': this.variant === 'info',
        'rs-avatar--light': this.variant === 'light',
        'rs-avatar--dark': this.variant === 'dark',
      };
    },
  },
});
</script>

<style lang="scss" scoped>
  @import '@/styles/_variables.scss';
  $avatarSize: 40px;
  .rs-avatar{
    position: relative;
    display: inline-flex;
    justify-content: center;
    align-items: center;
    width: $avatarSize;
    height: $avatarSize;
    border-radius: 50%;
    font-size: 1.5rem;
    text-align: center;
    color: $white;
    overflow: hidden;
    @each $name, $color in $colors {
      &--#{$name} {
        background-color: $color;
        &.rs-avatar--soft{
          color: $color;
          background-color: rgba($color, 0.28);
        }
      }

    }
    & > img{
      width: 100%;
      height: 100%;
      object-fit: cover;
      object-position: top left;
    }
    &--square{
      border-radius: $global-radius;
      & > img{
        border-radius: 0;
      }
    }

  }
</style>
